import React, { useState, useEffect, useRef } from "react";
import { FiX, FiCamera, FiDownload, FiFile } from "react-icons/fi";
import { graphqlRequest } from "../../../utils/graphqlRequest";
import { uploadToSignedUrl } from "../../../utils/uploadToSignedUrl";
import { toast } from "react-toastify";
import "../styles/EditMemberModal.css";
import {
  getBsbError,
  getTfnError,
  getBsbErrorRequired,
  getTfnErrorRequired,
  formatBSB,
  formatAbnDigits,
  getAbnError,
  getAbnErrorRequired,
  formatDigitsOnly,
  formatTfnDigits,
} from "../utils/australianTaxValidators";

/** HTML date inputs need YYYY-MM-DD; API often returns ISO timestamps. */
function toDateInputValue(value) {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const isoDay = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDay) return isoDay[1];
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** HTML time inputs need HH:MM */
function toTimeInputValue(value) {
  if (value == null || value === "") return "";
  const match = String(value).trim().match(/(\d{1,2}):(\d{2})/);
  if (!match) return "";
  return `${String(match[1]).padStart(2, "0")}:${match[2]}`;
}

function toGenderSelectValue(value) {
  if (!value) return "";
  const lower = String(value).toLowerCase();
  if (lower === "male" || String(value).toUpperCase() === "MALE") return "MALE";
  if (lower === "female" || String(value).toUpperCase() === "FEMALE") return "FEMALE";
  if (lower === "other" || String(value).toUpperCase() === "OTHER") return "OTHER";
  return "";
}

function resolveUploadUrl(rawUpload) {
  if (!rawUpload) return "";
  if (typeof rawUpload === "string") {
    const value = rawUpload.trim();
    if (!value) return "";
    if (value.startsWith("{") && value.endsWith("}")) {
      try {
        return resolveUploadUrl(JSON.parse(value));
      } catch {
        /* ignore */
      }
    }
    if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:")) return value;
    if (value.startsWith("/")) return `${window.location.origin}${value}`;
    const bucket = process.env.REACT_APP_AWS_BUCKET_NAME;
    const region = process.env.REACT_APP_AWS_REGION;
    if (bucket && region) {
      return `https://${bucket}.s3.${region}.amazonaws.com/${value}`;
    }
    return "";
  }
  if (typeof rawUpload === "object") {
    return (
      resolveUploadUrl(rawUpload.url || rawUpload.dataUrl || rawUpload.key || "") ||
      ""
    );
  }
  return "";
}

function formatDocDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function initialsFrom(firstName, lastName, fallback = "") {
  const a = String(firstName || "").trim().charAt(0);
  const b = String(lastName || "").trim().charAt(0);
  const pair = `${a}${b}`.toUpperCase();
  if (pair) return pair;
  const parts = String(fallback || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

const GET_WORKFORCE_DOCUMENTS = `
  query GetWorkforceDocumentsForUser($userId: String, $employeeCode: String) {
    getWorkforceDocuments(userId: $userId, employeeCode: $employeeCode) {
      id
      userId
      employeeCode
      employeeName
      documentType
      cleaningLicense
      experienceYears
      fileName
      fileKey
      fileUrl
      fileType
      fileSize
      createdAt
    }
  }
`;

const GENERATE_UPLOAD_URL = `
  mutation GenerateUrlForImageUpload($files: [Fileupload!]!) {
    generateUrlForImageUploadClient(files: $files) {
      key
      url
    }
  }
`;

export default function EditMemberModal({ member, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profileComplete, setProfileComplete] = useState(true);
  const [photoUrl, setPhotoUrl] = useState("");
  const [employeeCode, setEmployeeCode] = useState("");
  const [documents, setDocuments] = useState([]);
  const fileInputRef = useRef(null);

  const isContractor = (member?.title || "").toLowerCase() === "contractor";

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    businessName: "",
    email: "",
    contact: "",
    DOB: "",
    address: "",
    gender: "",
    taxResidency: "",
    visaStatus: "",
    visaExpiryDay: "",
    otherVisaType: "",
    emergencyContactName: "",
    emergencyContactNumber: "",
    bsb: "",
    accountNumber: "",
    accountName: "",
    taxFileNumber: "",
    abnNumber: "",
    claimTaxFreeThreshold: "",
    fundName: "",
    spin: "",
    fundABN: "",
    fundTelephoneNumber: "",
    memberNumber: "",
    superannuationAccountNumber: "",
    jobTitle: "",
    payType: "",
    perHourRate: "",
    superAnnuation: "",
    salaryTerm: "",
    typeOfWork: "",
    paydayDay: "",
    paydayTime: "",
    joiningDate: "",
  });

  const [fieldErrors, setFieldErrors] = useState({
    bsb: "",
    taxFileNumber: "",
    abnNumber: "",
    fundABN: "",
  });

  useEffect(() => {
    if (!member?.dbId) return;
    const fetchUser = async () => {
      try {
        setLoading(true);
        setPhotoUrl(resolveUploadUrl(member.photo) || "");
        setEmployeeCode(member.code || "");

        const queryWithVisaFields = `
          query GetSingleUser($GetUserInput: GetUserInput!) {
            getSingleUser(GetUserInput: $GetUserInput) {
              id firstName lastName businessName email contact DOB address gender
              taxResidency visaStatus visaExpiryDay otherVisaType emergencyContactName emergencyContactNumber
              bsb accountNumber accountName taxFileNumber abnNumber claimTaxFreeThreshold
              fundName spin fundABN fundTelephoneNumber memberNumber superannuationAccountNumber
              jobTitle payType perHourRate superAnnuation salaryTerm typeOfWork
              paydayDay paydayTime joiningDate roles profileComplete upload employeeCode
            }
          }
        `;
        const queryWithoutVisaFields = `
          query GetSingleUser($GetUserInput: GetUserInput!) {
            getSingleUser(GetUserInput: $GetUserInput) {
              id firstName lastName businessName email contact DOB address gender
              taxResidency emergencyContactName emergencyContactNumber
              bsb accountNumber accountName taxFileNumber abnNumber claimTaxFreeThreshold
              fundName spin fundABN fundTelephoneNumber memberNumber superannuationAccountNumber
              jobTitle payType perHourRate superAnnuation salaryTerm typeOfWork
              paydayDay paydayTime joiningDate roles profileComplete upload employeeCode
            }
          }
        `;

        let data;
        try {
          data = await graphqlRequest(queryWithVisaFields, {
            GetUserInput: { id: member.dbId },
          });
        } catch {
          data = await graphqlRequest(queryWithoutVisaFields, {
            GetUserInput: { id: member.dbId },
          });
        }

        const u = data?.getSingleUser;
        if (u) {
          setProfileComplete(u.profileComplete !== false);
          const code =
            u.employeeCode ||
            member.code ||
            `EMP-${String(u.id).slice(0, 4).toUpperCase()}`;
          setEmployeeCode(code);
          setPhotoUrl(resolveUploadUrl(u.upload) || resolveUploadUrl(member.photo) || "");
          setFormData({
            firstName: u.firstName || "",
            lastName: u.lastName || "",
            businessName: u.businessName || "",
            email: u.email || "",
            contact: u.contact || "",
            DOB: toDateInputValue(u.DOB),
            address: u.address || "",
            gender: toGenderSelectValue(u.gender),
            taxResidency: u.taxResidency || "",
            visaStatus: u.visaStatus || "",
            visaExpiryDay: toDateInputValue(u.visaExpiryDay),
            otherVisaType: u.otherVisaType || "",
            emergencyContactName: u.emergencyContactName || "",
            emergencyContactNumber: u.emergencyContactNumber || "",
            bsb: formatBSB(u.bsb || ""),
            accountNumber: u.accountNumber || "",
            accountName: u.accountName || "",
            taxFileNumber: u.taxFileNumber || "",
            abnNumber: u.abnNumber || "",
            claimTaxFreeThreshold: u.claimTaxFreeThreshold || "",
            fundName: u.fundName || "",
            spin: u.spin || "",
            fundABN: u.fundABN || "",
            fundTelephoneNumber: u.fundTelephoneNumber || "",
            memberNumber: u.memberNumber || "",
            superannuationAccountNumber: u.superannuationAccountNumber || "",
            jobTitle: u.jobTitle || "",
            payType: u.payType || "",
            perHourRate: u.perHourRate != null ? String(u.perHourRate) : "",
            superAnnuation: u.superAnnuation != null ? String(u.superAnnuation) : "",
            salaryTerm: u.salaryTerm || "",
            typeOfWork: u.typeOfWork || "",
            paydayDay: u.paydayDay || "",
            paydayTime: toTimeInputValue(u.paydayTime),
            joiningDate: toDateInputValue(u.joiningDate),
          });

          try {
            const docsData = await graphqlRequest(GET_WORKFORCE_DOCUMENTS, {
              userId: member.dbId,
              employeeCode: code,
            });
            setDocuments(
              Array.isArray(docsData?.getWorkforceDocuments)
                ? docsData.getWorkforceDocuments
                : [],
            );
          } catch (docsErr) {
            console.error("Failed to load workforce documents:", docsErr);
            setDocuments([]);
          }
        }
      } catch (err) {
        console.error("Error fetching user for edit:", err);
        toast.error("Failed to load member data");
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [member]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "bsb") {
      setFormData((prev) => ({ ...prev, bsb: formatBSB(value) }));
    } else if (name === "abnNumber" || name === "fundABN") {
      const formatted = formatAbnDigits(value);
      setFormData((prev) => ({ ...prev, [name]: formatted }));
      setFieldErrors((prev) => ({
        ...prev,
        [name]: getAbnError(formatted) || "",
      }));
    } else if (name === "taxFileNumber") {
      const formatted = formatTfnDigits(value);
      setFormData((prev) => ({ ...prev, taxFileNumber: formatted }));
      setFieldErrors((prev) => ({
        ...prev,
        taxFileNumber: getTfnError(formatted) || "",
      }));
    } else if (name === "memberNumber") {
      setFormData((prev) => ({ ...prev, memberNumber: formatDigitsOnly(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  useEffect(() => {
    if (loading) return;
    setFieldErrors((prev) => ({
      ...prev,
      bsb: getBsbError(formData.bsb) || "",
      taxFileNumber: !isContractor ? getTfnError(formData.taxFileNumber) || "" : "",
      abnNumber: isContractor ? getAbnError(formData.abnNumber) || "" : "",
      fundABN: !isContractor ? getAbnError(formData.fundABN) || "" : "",
    }));
  }, [
    loading,
    isContractor,
    formData.bsb,
    formData.taxFileNumber,
    formData.abnNumber,
    formData.fundABN,
  ]);

  const handleChoosePhoto = () => {
    if (uploadingPhoto) return;
    fileInputRef.current?.click();
  };

  const handleUploadPhoto = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = "";
    if (!file || !member?.dbId) return;
    if (!String(file.type || "").startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8MB");
      return;
    }

    try {
      setUploadingPhoto(true);
      const uploadData = await graphqlRequest(GENERATE_UPLOAD_URL, {
        files: [{ fileName: file.name, contentType: file.type }],
      });
      const target = uploadData?.generateUrlForImageUploadClient?.[0];
      if (!target?.url || !target?.key) {
        throw new Error("Failed to prepare photo upload");
      }

      const stored = await uploadToSignedUrl(target.url, target.key, file);

      const mutation = `
        mutation UpdateUserByAdmin($id: String!, $input: UpdateUserByAdmin!) {
          updateUserByAdmin(id: $id, UpdateUserByAdmin: $input) {
            id upload
          }
        }
      `;
      const updated = await graphqlRequest(mutation, {
        id: member.dbId,
        input: { upload: stored },
      });
      const nextUrl =
        resolveUploadUrl(updated?.updateUserByAdmin?.upload) ||
        resolveUploadUrl(stored) ||
        URL.createObjectURL(file);
      setPhotoUrl(nextUrl);
      toast.success("Profile photo updated");
      onSaved?.();
    } catch (err) {
      console.error("Photo upload failed:", err);
      toast.error(err?.message || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const downloadDocument = (url, fileName) => {
    const href = resolveUploadUrl(url);
    if (!href) {
      toast.error("File unavailable");
      return;
    }
    const a = document.createElement("a");
    a.href = href;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.download = fileName || "document";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const bsbErr = getBsbErrorRequired(formData.bsb);
    const tfnErr = !isContractor ? getTfnErrorRequired(formData.taxFileNumber) : null;
    const abnErr = isContractor
      ? getAbnErrorRequired(formData.abnNumber)
      : getAbnErrorRequired(formData.fundABN);
    setFieldErrors({
      bsb: bsbErr || "",
      taxFileNumber: tfnErr || "",
      abnNumber: isContractor ? abnErr || "" : "",
      fundABN: !isContractor ? abnErr || "" : "",
    });
    if (bsbErr || tfnErr || abnErr) {
      toast.error("Please fix BSB / Tax File Number / ABN before saving.");
      return;
    }
    try {
      setSaving(true);
      const mutation = `
        mutation UpdateUserByAdmin($id: String!, $input: UpdateUserByAdmin!) {
          updateUserByAdmin(id: $id, UpdateUserByAdmin: $input) {
            id firstName lastName email contact
          }
        }
      `;

      const input = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        contact: formData.contact,
        DOB: formData.DOB || undefined,
        address: formData.address || undefined,
        gender: formData.gender || undefined,
        taxResidency: formData.taxResidency || undefined,
        visaStatus: formData.visaStatus || undefined,
        visaExpiryDay: formData.visaExpiryDay || undefined,
        otherVisaType: formData.otherVisaType || undefined,
        emergencyContactName: formData.emergencyContactName || undefined,
        emergencyContactNumber: formData.emergencyContactNumber || undefined,
        bsb: formData.bsb || undefined,
        accountNumber: formData.accountNumber || undefined,
        accountName: formData.accountName || undefined,
        paydayDay: formData.paydayDay || undefined,
        paydayTime: formData.paydayTime || undefined,
        joiningDate: formData.joiningDate || undefined,
        perHourRate: formData.perHourRate ? parseFloat(formData.perHourRate) : undefined,
      };

      if (isContractor) {
        input.businessName = formData.businessName || undefined;
        input.abnNumber = formData.abnNumber || undefined;
        input.payType = formData.payType || undefined;
        input.typeOfWork = formData.typeOfWork || undefined;
      } else {
        input.taxFileNumber = formData.taxFileNumber || undefined;
        input.claimTaxFreeThreshold = formData.claimTaxFreeThreshold || undefined;
        input.fundName = formData.fundName || undefined;
        input.spin = formData.spin || undefined;
        input.fundABN = formData.fundABN || undefined;
        input.fundTelephoneNumber = formData.fundTelephoneNumber || undefined;
        input.memberNumber = formData.memberNumber || undefined;
        input.superannuationAccountNumber =
          formData.superannuationAccountNumber || undefined;
        input.jobTitle = formData.jobTitle || undefined;
        input.superAnnuation = formData.superAnnuation
          ? parseFloat(formData.superAnnuation)
          : undefined;
        input.salaryTerm = formData.salaryTerm || undefined;
        input.typeOfWork = formData.typeOfWork || undefined;
      }

      await graphqlRequest(mutation, { id: member.dbId, input });
      toast.success("Member updated successfully!");
      onSaved?.();
      onClose();
    } catch (err) {
      console.error("Error updating member:", err);
      toast.error(err?.message || "Failed to update member");
    } finally {
      setSaving(false);
    }
  };

  if (!member) return null;

  const displayName =
    [formData.firstName, formData.lastName].filter(Boolean).join(" ") ||
    member.name ||
    "Employee";
  const roleLabel = member.title || "Staff";

  return (
    <div className="edit-member-overlay" onClick={onClose}>
      <div className="edit-member-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-member-header">
          <h2>Employee Profile</h2>
          <button type="button" className="edit-member-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        {loading ? (
          <div className="edit-member-loading">Loading member data...</div>
        ) : (
          <form onSubmit={handleSave} className="edit-member-body">
            {!profileComplete ? (
              <div className="edit-profile-incomplete">
                Profile incomplete — employee still needs to finish personal /
                banking details in the employee portal. You can fill or update
                any remaining fields here.
              </div>
            ) : null}

            <section className="edit-profile-summary">
              <div className="edit-profile-avatar-wrap">
                <div className="edit-profile-avatar">
                  {photoUrl ? (
                    <img src={photoUrl} alt={displayName} />
                  ) : (
                    <span>
                      {initialsFrom(
                        formData.firstName,
                        formData.lastName,
                        displayName,
                      )}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="edit-profile-photo-btn"
                  onClick={handleChoosePhoto}
                  disabled={uploadingPhoto}
                  title="Change profile photo"
                >
                  <FiCamera size={14} />
                  {uploadingPhoto ? "Uploading…" : "Photo"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="edit-hidden-file"
                  onChange={handleUploadPhoto}
                />
              </div>
              <div className="edit-profile-summary-text">
                <h3>{displayName}</h3>
                <p>Employee ID: {employeeCode || member.code || "—"}</p>
                <p>{roleLabel}</p>
              </div>
            </section>

            {/* Personal Info */}
            <section className="edit-section">
              <h3>Personal Info</h3>
              <div className="edit-grid">
                <div className="edit-field">
                  <label>First Name *</label>
                  <input name="firstName" value={formData.firstName} onChange={handleChange} required />
                </div>
                <div className="edit-field">
                  <label>Last Name *</label>
                  <input name="lastName" value={formData.lastName} onChange={handleChange} required />
                </div>
                {isContractor && (
                  <div className="edit-field">
                    <label>Business Name</label>
                    <input name="businessName" value={formData.businessName} onChange={handleChange} />
                  </div>
                )}
                <div className="edit-field">
                  <label>Email *</label>
                  <input name="email" type="email" value={formData.email} onChange={handleChange} required />
                </div>
                <div className="edit-field">
                  <label>Contact Number</label>
                  <input name="contact" value={formData.contact} onChange={handleChange} />
                </div>
                <div className="edit-field">
                  <label>Date of Birth</label>
                  <input name="DOB" type="date" value={formData.DOB} onChange={handleChange} />
                </div>
                <div className="edit-field">
                  <label>Gender</label>
                  <select name="gender" value={formData.gender} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div className="edit-field edit-field-full">
                  <label>Address</label>
                  <input name="address" value={formData.address} onChange={handleChange} />
                </div>
                {!isContractor && (
                  <div className="edit-field">
                    <label>Tax Residency</label>
                    <select name="taxResidency" value={formData.taxResidency} onChange={handleChange}>
                      <option value="">Select</option>
                      <option value="australian-resident">An Australian resident for tax purposes</option>
                      <option value="foreign-resident">A foreign resident for tax purposes</option>
                      <option value="working-holiday-maker">A working holiday maker</option>
                    </select>
                  </div>
                )}
                {!isContractor && (
                  <div className="edit-field">
                    <label>Visa Status</label>
                    <select name="visaStatus" value={formData.visaStatus} onChange={handleChange}>
                      <option value="">Select Visa Status</option>
                      <option value="permanent-resident">Permanent Resident</option>
                      <option value="student">Student</option>
                      <option value="tourist">Tourist</option>
                      <option value="citizen">Citizen</option>
                      <option value="working-holiday">Working Holiday</option>
                      <option value="others">Others</option>
                    </select>
                  </div>
                )}
                {!isContractor && formData.visaStatus && formData.visaStatus !== "citizen" && (
                  <div className="edit-field">
                    <label>Visa Expiry Date</label>
                    <input
                      name="visaExpiryDay"
                      type="date"
                      value={formData.visaExpiryDay}
                      onChange={handleChange}
                    />
                  </div>
                )}
                {!isContractor && formData.visaStatus === "others" && (
                  <div className="edit-field">
                    <label>Visa Type</label>
                    <input
                      name="otherVisaType"
                      value={formData.otherVisaType}
                      onChange={handleChange}
                      placeholder="Enter visa type"
                    />
                  </div>
                )}
                <div className="edit-field">
                  <label>Emergency Contact Name</label>
                  <input name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange} />
                </div>
                <div className="edit-field">
                  <label>Emergency Contact Number</label>
                  <input name="emergencyContactNumber" value={formData.emergencyContactNumber} onChange={handleChange} />
                </div>
              </div>
            </section>

            {/* Financial Info */}
            <section className="edit-section">
              <h3>Financial Info</h3>
              <div className="edit-grid">
                <div className="edit-field">
                  <label>BSB</label>
                  <div className="edit-field-input-wrap">
                    <input
                      name="bsb"
                      value={formData.bsb}
                      onChange={handleChange}
                      maxLength={7}
                      placeholder="e.g. 062-000"
                      autoComplete="off"
                      className={fieldErrors.bsb ? "edit-input-error" : ""}
                      aria-invalid={!!fieldErrors.bsb}
                    />
                    {fieldErrors.bsb ? (
                      <p className="edit-field-error" role="alert">
                        {fieldErrors.bsb}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="edit-field">
                  <label>Account Number</label>
                  <input name="accountNumber" value={formData.accountNumber} onChange={handleChange} />
                </div>
                <div className="edit-field">
                  <label>Account Name</label>
                  <input name="accountName" value={formData.accountName} onChange={handleChange} />
                </div>
                {!isContractor && (
                  <div className="edit-field">
                    <label>Tax File Number</label>
                    <div className="edit-field-input-wrap">
                      <input
                        name="taxFileNumber"
                        value={formData.taxFileNumber}
                        onChange={handleChange}
                        maxLength={9}
                        inputMode="numeric"
                        placeholder="Enter TFN (9 digits)"
                        pattern="[0-9]{9}"
                        autoComplete="off"
                        className={fieldErrors.taxFileNumber ? "edit-input-error" : ""}
                        aria-invalid={!!fieldErrors.taxFileNumber}
                      />
                      {fieldErrors.taxFileNumber ? (
                        <p className="edit-field-error" role="alert">
                          {fieldErrors.taxFileNumber}
                        </p>
                      ) : null}
                    </div>
                  </div>
                )}
                {isContractor && (
                  <div className="edit-field">
                    <label>ABN Number</label>
                    <input
                      name="abnNumber"
                      value={formData.abnNumber}
                      onChange={handleChange}
                      inputMode="numeric"
                      maxLength="11"
                      placeholder="Enter ABN (11 digits)"
                      className={fieldErrors.abnNumber ? "edit-input-error" : ""}
                      aria-invalid={!!fieldErrors.abnNumber}
                    />
                    {fieldErrors.abnNumber ? (
                      <p className="edit-field-error" role="alert">
                        {fieldErrors.abnNumber}
                      </p>
                    ) : null}
                  </div>
                )}
                {!isContractor && (
                  <div className="edit-field">
                    <label>Claim Tax-Free Threshold</label>
                    <select name="claimTaxFreeThreshold" value={formData.claimTaxFreeThreshold} onChange={handleChange}>
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                )}
              </div>
            </section>

            {/* Superannuation Info — only for non-Contractor */}
            {!isContractor && (
              <section className="edit-section">
                <h3>Superannuation Info</h3>
                <div className="edit-grid">
                  <div className="edit-field">
                    <label>Fund Name</label>
                    <input name="fundName" value={formData.fundName} onChange={handleChange} />
                  </div>
                  <div className="edit-field">
                    <label>Unique Superannuation Identifier (USI)</label>
                    <input
                      name="spin"
                      value={formData.spin}
                      onChange={handleChange}
                      placeholder="Enter USI"
                    />
                  </div>
                  <div className="edit-field">
                    <label>Fund ABN</label>
                    <input
                      name="fundABN"
                      value={formData.fundABN}
                      onChange={handleChange}
                      inputMode="numeric"
                      maxLength="11"
                      placeholder="Enter ABN (11 digits)"
                      className={fieldErrors.fundABN ? "edit-input-error" : ""}
                      aria-invalid={!!fieldErrors.fundABN}
                    />
                    {fieldErrors.fundABN ? (
                      <p className="edit-field-error" role="alert">
                        {fieldErrors.fundABN}
                      </p>
                    ) : null}
                  </div>
                  <div className="edit-field">
                    <label>Fund Telephone</label>
                    <input name="fundTelephoneNumber" value={formData.fundTelephoneNumber} onChange={handleChange} />
                  </div>
                  <div className="edit-field">
                    <label>Member Number</label>
                    <input
                      name="memberNumber"
                      value={formData.memberNumber}
                      onChange={handleChange}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Enter member number"
                    />
                  </div>
                  <div className="edit-field">
                    <label>Superannuation Account Number</label>
                    <input
                      name="superannuationAccountNumber"
                      value={formData.superannuationAccountNumber}
                      onChange={handleChange}
                      placeholder="Enter account number"
                    />
                  </div>
                </div>
              </section>
            )}

            {/* Professional Info */}
            <section className="edit-section">
              <h3>Professional Info</h3>
              <div className="edit-grid">
                {!isContractor && (
                  <div className="edit-field">
                    <label>Level</label>
                    <select name="jobTitle" value={formData.jobTitle} onChange={handleChange}>
                      <option value="">Select Level</option>
                      <option value="Junior">Junior</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Senior">Senior</option>
                    </select>
                  </div>
                )}
                {isContractor && (
                  <div className="edit-field">
                    <label>Pay Type</label>
                    <select name="payType" value={formData.payType} onChange={handleChange}>
                      <option value="">Select Pay Type</option>
                      <option value="pay-per-hour">Pay per hour</option>
                      <option value="pay-per-project">Pay per project</option>
                    </select>
                  </div>
                )}
                <div className="edit-field">
                  <label>Per Hour Rate ($)</label>
                  <input name="perHourRate" type="number" step="0.01" value={formData.perHourRate} onChange={handleChange} />
                </div>
                {!isContractor && (
                  <div className="edit-field">
                    <label>Super Annuation (%)</label>
                    <input name="superAnnuation" type="number" step="0.01" value={formData.superAnnuation} onChange={handleChange} />
                  </div>
                )}
                {!isContractor && (
                  <div className="edit-field">
                    <label>Salary Term</label>
                    <select name="salaryTerm" value={formData.salaryTerm} onChange={handleChange}>
                      <option value="">Select Salary Term</option>
                      <option value="hourly">Hourly</option>
                      <option value="weekly">Weekly</option>
                      <option value="fortnightly">Fortnightly</option>
                      <option value="monthly">Monthly</option>
                      <option value="annually">Annually</option>
                    </select>
                  </div>
                )}
                <div className="edit-field">
                  <label>Type of Work</label>
                  <select name="typeOfWork" value={formData.typeOfWork} onChange={handleChange}>
                    <option value="">Select Type of Work</option>
                    <option value="casual">Casual</option>
                    <option value="part-time">Part-time</option>
                    <option value="full-time">Full-time</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>
                <div className="edit-field">
                  <label>Payday (Day)</label>
                  <select name="paydayDay" value={formData.paydayDay} onChange={handleChange}>
                    <option value="">Select Day</option>
                    <option value="sunday">Sunday</option>
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                  </select>
                </div>
                <div className="edit-field">
                  <label>Payday (Time)</label>
                  <input name="paydayTime" type="time" value={formData.paydayTime} onChange={handleChange} />
                </div>
                <div className="edit-field">
                  <label>Joining Date</label>
                  <input name="joiningDate" type="date" value={formData.joiningDate} onChange={handleChange} />
                </div>
              </div>
            </section>

            {/* Documents (same as employee portal profile) */}
            <section className="edit-section">
              <h3>Uploaded Documents</h3>
              <p className="edit-docs-sub">
                {documents.length} document{documents.length === 1 ? "" : "s"} on
                file
                {photoUrl ? " · includes profile photo" : ""}
              </p>
              <div className="edit-docs-list">
                {photoUrl ? (
                  <article className="edit-doc-card">
                    <div className="edit-doc-icon" aria-hidden>
                      <FiFile size={18} />
                    </div>
                    <div className="edit-doc-meta">
                      <strong>Profile Photo</strong>
                      <span>Profile image</span>
                    </div>
                    <button
                      type="button"
                      className="edit-doc-download"
                      onClick={() => downloadDocument(photoUrl, "profile-photo")}
                    >
                      <FiDownload size={14} /> Download
                    </button>
                  </article>
                ) : null}
                {documents.map((doc) => {
                  const fileUrl = resolveUploadUrl(doc?.fileUrl || doc?.fileKey);
                  const uploaded = formatDocDate(doc?.createdAt);
                  return (
                    <article className="edit-doc-card" key={doc.id}>
                      <div className="edit-doc-icon" aria-hidden>
                        <FiFile size={18} />
                      </div>
                      <div className="edit-doc-meta">
                        <strong title={doc?.documentType || "Document"}>
                          {doc?.documentType || "Document"}
                        </strong>
                        <span title={doc?.fileName || ""}>
                          {doc?.fileName || "File"}
                          {uploaded ? ` · ${uploaded}` : ""}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="edit-doc-download"
                        onClick={() =>
                          downloadDocument(
                            fileUrl,
                            doc?.fileName || doc?.documentType || "document",
                          )
                        }
                        disabled={!fileUrl}
                      >
                        <FiDownload size={14} /> Download
                      </button>
                    </article>
                  );
                })}
                {!photoUrl && documents.length === 0 ? (
                  <div className="edit-docs-empty">No documents uploaded yet</div>
                ) : null}
              </div>
            </section>

            {/* Actions */}
            <div className="edit-member-actions">
              <button type="submit" className="edit-save-btn" disabled={saving || uploadingPhoto}>
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <button type="button" className="edit-cancel-btn" onClick={onClose} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
